import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import trackEvent from 'actions/analytics/trackEvent.action';
import { beFindReaderV1 } from 'actions/pos/beFindReader.action';
import { allFieldsPrice } from 'bl/fields.bl';
import DepositPrompt from 'components/booking/prompt/DepositPrompt.component';
import PromptContent from 'components/booking/prompt/PromptContent.component';
import withSalonCurrency from 'components/hoc/withSalonCurrency.hoc';
import CurrencySign from 'components/price/CurrencySign.component';
import DEPOSIT_SOURCE from 'constants/payment/depositSource.const';
import { useTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonId, selectCurrentSalonPosFlow } from 'selectors/currentSalon.selectors';
import getString from 'utils/accessors/getString.util';
import fromAnyToCoinNum from 'utils/converters/any/fromAnyToCoinNum.util';
import { fromEventToValue } from 'utils/converters/event.util';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import currencySign from 'utils/intl/currencySign.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isNotEnumVal from 'utils/predicates/enum/isNotEnumVal.util';
import isNegativeNumber from 'utils/predicates/number/isNegativeNumber.util';
import isPositiveNumber from 'utils/predicates/number/isPositiveNumber.util';

import { createDepositFrontEnd } from 'booking/actions';
import BackButton from 'booking/components/common/bw-header/BackButton';
import HeaderWrapper from 'booking/components/common/bw-header/HeaderWrapper';
import BWContent from 'booking/components/common/BWContent';
import PageTitle from 'booking/components/common/PageTitle';
import RadioFormContainer from 'onboarding/common/RadioForm';

import CN from './CreateDeposit.module.scss';

const CTAG = 'CreateDeposit';
const L = logger(CTAG);

const DEPOSIT_OPTION = Object.freeze({
  cash: DEPOSIT_SOURCE.cash,
  card: `${DEPOSIT_SOURCE.pos}_or_${DEPOSIT_SOURCE.card}`, // NOTE: can be anything unique, might as well be both
  other: DEPOSIT_SOURCE.other,
});

const plus = (a, b) => a + b;

const determineDepositInit = ({ deposit, totalPrice }) => {
  const number = fromAnyToNumber(deposit);
  return !number ? '' : ((number / totalPrice) * 100).toString();
};

const determineDisplayMessage = ({ isPositiveDeposit, depositAmount, depositPercentage, isFixedPrice, percentageCoinNumber, depositCoinNumber, currentSalonAbbr, currentSalonSign, t }) => {
  if (!(depositAmount && depositPercentage)) {
    return '';
  }

  if (!isPositiveDeposit || !depositPercentage) {
    return '';
  }

  const value = currencySign({ value: depositCoinNumber, currentSalonAbbr, currentSalonSign });

  if (isFixedPrice) {
    const percent = depositPercentage ? percentageCoinNumber : 0;
    return t('scheduler.createDeposit.displayMsg1', {
      value,
      percent,
    });
  }

  return t('scheduler.createDeposit.displayMsg2', {
    percentageCoinNumber,
    value,
  });
};

const CreateDeposit = (props) => {
  const { addedServices, posFlow, clientId, createDepositFrontEnd, deposit, trackEvent, currentSalonAbbr, currentSalonSign } = props;

  const { t } = useTranslation();

  const isPosEnabled = posFlow === true;
  const validServices = addedServices.filter((service) => service && !service.deleted);

  const [depositAmount, setDepositAmount] = useState(fromAnyToString(deposit));
  const [depositErrorMessage, setDepositErrorMessage] = useState('');
  const [depositSource, setDepositSource] = useState('');
  const [totalPrice, setTotalPrice] = useState(allFieldsPrice(validServices).reduce(plus, 0));

  const [sourceErrorMessage, setSourceErrorMessage] = useState('');
  const [depositPercentage, setDepositPercentage] = useState(determineDepositInit({ deposit, totalPrice }));

  // TODO: replace these boolean hooks with useTip
  const [isDepositTouched, setIsDepositTouched] = useState(false);
  const [isFixedPrice, setIsFixedPrice] = useState(true);
  const [isCardSource, setIsCardSource] = useState(false);
  const [isCashSource, setIsCashSource] = useState(false);
  const [isOtherSource, setIsOtherSource] = useState(false);

  const depositCoinNumber = fromAnyToCoinNum(depositAmount);
  const isPositiveDeposit = isPositiveNumber(depositCoinNumber);
  const isNegativeDeposit = isNegativeNumber(depositCoinNumber);
  const percentageCoinNumber = fromAnyToCoinNum(depositPercentage);

  const displayDeposit = isFixedPrice ? depositAmount : depositPercentage;
  const displayMessage = determineDisplayMessage({
    isPositiveDeposit,
    depositAmount,
    depositPercentage,
    isFixedPrice,
    percentageCoinNumber,
    depositCoinNumber,
    currentSalonAbbr,
    currentSalonSign,
    t,
  });

  const handleBack = useCallback(() => void navFor(CTAG, 'handleBack()', props).back(), [props]);

  const handleChooseFixed = useCallback(() => {
    setDepositPercentage(percentageCoinNumber);
    setDepositAmount(depositCoinNumber);
    setIsFixedPrice(true);
  }, [depositCoinNumber, percentageCoinNumber]);

  const handleChoosePercent = useCallback(() => {
    setDepositAmount(depositCoinNumber);
    setDepositPercentage(percentageCoinNumber);
    setIsFixedPrice(false);
  }, [depositCoinNumber, percentageCoinNumber]);

  const handleRadio = useCallback(
    (ev) => {
      setSourceErrorMessage('');

      const option = getString('target.id', ev);

      if (option === DEPOSIT_OPTION.cash) {
        setIsCardSource(false);
        setIsOtherSource(false);
        setIsCashSource(true);
        setDepositSource('cash');
      }

      if (option === DEPOSIT_OPTION.card) {
        setIsCardSource(true);
        setIsOtherSource(false);
        setIsCashSource(false);
        setDepositSource(isPosEnabled ? DEPOSIT_SOURCE.pos : DEPOSIT_SOURCE.card);
      }

      if (option === DEPOSIT_OPTION.other) {
        setIsCardSource(false);
        setIsOtherSource(true);
        setIsCashSource(false);
        setDepositSource(DEPOSIT_SOURCE.other);
      }

      if (isNotEnumVal(DEPOSIT_OPTION, option)) {
        L.error('handleRadio()', 'Invalid option:', option, 'for DEPOSIT_OPTION:', DEPOSIT_OPTION);
      }
    },
    [isPosEnabled],
  );

  const handleSubmit = useCallback(() => {
    if (isCardSource === false && isOtherSource === false && isCashSource === false) {
      return void setSourceErrorMessage(t('scheduler.createDeposit.sourceErrorMessage1'));
    }

    if (!isPositiveDeposit || isNegativeDeposit) {
      return void setSourceErrorMessage(t('scheduler.createDeposit.sourceErrorMessage2'));
    }

    trackEvent({ event: 'CreateDepositCompleted', properties: { type: isFixedPrice ? 'fixed' : 'percentage' } });

    createDepositFrontEnd({
      amount: depositCoinNumber,
      depositType: 'deposited',
      notes: '',
      clientId,
      used_for_appointmentIds: null,
      source: depositSource,
      slickPosReader: isCardSource && isPosEnabled,
    });

    return void navFor(CTAG, 'handleSubmit()', props).back();
  }, [
    isCardSource,
    isOtherSource,
    isCashSource,
    isPositiveDeposit,
    isNegativeDeposit,
    trackEvent,
    isFixedPrice,
    createDepositFrontEnd,
    depositCoinNumber,
    clientId,
    depositSource,
    isPosEnabled,
    props,
    t,
  ]);

  const handleBlur = useCallback(
    (ev) => {
      const deposit = fromEventToValue(ev);
      if (isDepositTouched) {
        setDepositErrorMessage(isPositiveNumber(fromAnyToCoinNum(deposit)) ? '' : t('scheduler.createDeposit.depositErrorMsg'));
        setIsDepositTouched(false);
      } else {
        setIsDepositTouched(true);
      }
    },
    [isDepositTouched, t],
  );

  const handleChange = useCallback(
    (ev) => {
      const value = fromEventToValue(ev);
      setDepositErrorMessage('');

      const total = fromAnyToNumber(totalPrice);

      if (!isPositiveNumber(total)) {
        setDepositAmount(value);
        setDepositPercentage('');
        return;
      }

      const price = fromAnyToNumber(value);

      if (isFixedPrice) {
        setDepositAmount(value);
        setDepositPercentage(((price / total) * 100).toString());
      } else {
        setDepositPercentage(value);
        setDepositAmount((price / (100 / total)).toString());
      }
    },
    [isFixedPrice, totalPrice],
  );

  useEffect(() => void trackEvent({ event: 'CreateDepositViewed', properties: {} }), [trackEvent]);

  useEffect(() => {
    // FIXME: this useEffect isn't necessary, deal with the calculation where addedServices and/or deposit are input

    const { amount } = asObject(deposit);

    const totalPrice = addedServices.filter((service) => !service.deleted).reduce((total, service) => total + service.price, 0);

    if (totalPrice && amount) {
      setDepositPercentage(fromAnyToString((amount / totalPrice) * 100));
    }
    setTotalPrice(totalPrice);
  }, [addedServices, deposit]);

  if (!clientId) {
    return (
      <div className="BookingWindow">
        <HeaderWrapper>
          <BackButton onClick={handleBack} />
          <PageTitle pageTitle={t('scheduler.createDeposit.title1')} subTitle="" />
        </HeaderWrapper>
        <BWContent additionalClass="space">
          <div className={CN.header} data-cy="no-client-deposit-info">
            <p>{t('scheduler.createDeposit.noSelectedClientMsg')}</p>
          </div>
        </BWContent>
      </div>
    );
  }

  return (
    <div className="BookingWindow header-center deposit-booking">
      <HeaderWrapper>
        <BackButton onClick={handleBack} />
        <PageTitle pageTitle={t('scheduler.createDeposit.title2')} subTitle="" />
      </HeaderWrapper>

      <BWContent additionalClass="space">
        <div className="create-deposite-container cfix">
          <small>{t('scheduler.createDeposit.enterDepositLabel')}</small>
          <div className="form cfix">
            <label className="label" htmlFor="enter-deposite">
              <input id="enter-deposite" className="input-small input-default" type="number" pattern="[0-9]*" onChange={handleChange} onBlur={handleBlur} value={displayDeposit} placeholder="0" />
            </label>

            <div className="cb-group">
              <div className={isFixedPrice ? 'cb cb-selected' : 'cb'} onClick={handleChooseFixed} role="button" tabIndex={0}>
                <span className="cb-img" />
                <span className="text-label">
                  {t('scheduler.createDeposit.fixed')} (<CurrencySign />)
                </span>
              </div>

              <div className={isFixedPrice ? 'cb' : 'cb cb-selected'} onClick={handleChoosePercent} role="button" tabIndex={0}>
                <span className="cb-img" />
                <span className="text-label"> {t('scheduler.createDeposit.percent')}</span>
              </div>
            </div>
          </div>

          {depositErrorMessage !== '' && (
            <div>
              <span className={CN.styleError}>{depositErrorMessage}</span>
            </div>
          )}

          <p className="message">{displayMessage}</p>

          <div className="form cfix cash-or-card">
            <small>{t('scheduler.createDeposit.depositPaymentTypeTitle')}</small>
            <div className="row radio-group cb-group-payment">
              <RadioFormContainer
                selections={[
                  {
                    id: DEPOSIT_OPTION.cash,
                    label: t('scheduler.createDeposit.cash'),
                    name: 'payment',
                    checked: isCashSource,
                  },
                  {
                    id: DEPOSIT_OPTION.card,
                    label: isPosEnabled ? t('scheduler.createDeposit.slickPayReader') : t('scheduler.createDeposit.card'),
                    name: 'payment',
                    checked: isCardSource,
                  },
                  {
                    id: DEPOSIT_OPTION.other,
                    label: isPosEnabled ? t('scheduler.createDeposit.otherNonSlick') : t('scheduler.createDeposit.other'),
                    name: 'payment',
                    checked: isOtherSource,
                  },
                ]}
                onChange={handleRadio}
              />
            </div>
          </div>

          {sourceErrorMessage !== '' && (
            <div>
              <span className={CN.styleError}>{sourceErrorMessage}</span>
            </div>
          )}

          <button
            className={cn({
              [CN.saveDeposit]: true,
              btn: true,
              'btn-violet': true,
              'pull-right': true,
            })}
            onClick={handleSubmit}
          >
            {t('scheduler.createDeposit.saveButton')}
          </button>
        </div>

        <DepositPrompt>
          <PromptContent />
        </DepositPrompt>
      </BWContent>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => bindActionCreators({ beFindReader: beFindReaderV1, createDepositFrontEnd, trackEvent }, dispatch);

const mapStateToProps = (state) => ({
  addedServices: state.booking.addedServices,
  clientId: state.booking.client ? state.booking.client.id : void 1,
  deposit: state.booking.deposit,
  posFlow: selectCurrentSalonPosFlow(state),
  salonId: selectCurrentSalonId(state),
});

const CreateDepositHoc = connect(mapStateToProps, mapDispatchToProps)(withSalonCurrency(CreateDeposit));

export default CreateDepositHoc;
